import { AlertTriangle, X } from "lucide-react";
import Image from "next/image";
import { memo, useState } from "react";
import moment from "moment-timezone";
import CompanyBusinessInsight from "./CompanyBusinessInsight";

interface WhatCompanyDoesModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  selectedCompany: string;
  /** Reduced height when nested inside another modal (e.g. AnalyzeStockModal) */
  compact?: boolean;
}

const WhatCompanyDoesModal = ({
  show,
  setShow,
  selectedCompany,
  compact = false,
}: WhatCompanyDoesModalProps) => {
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  if (!show) return null;

  const heightClass = compact
    ? "h-[68vh] max-h-[68vh]"
    : "h-[88vh] max-h-[88vh]";
  /** Stay inside AnalyzeStockModal (max-w-[85vw]) on ≤1600px screens */
  const widthClass = compact
    ? "max-w-[min(78vw,72rem)]"
    : "max-w-[min(96vw,88rem)]";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-[2vw] py-4 !mt-0 backdrop-blur-md"
      onClick={() => setShow(false)}
    >
      <div
        className={`overflow-hidden shadow-2xl z-10 bg-white dark:bg-gray-800 rounded-xl w-full ${widthClass} ${heightClass} flex flex-col border border-[#d0d4e4] dark:border-[#394564]`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 py-3 flex items-center justify-between gap-3 border-b border-[#d0d4e4] dark:border-[#394564] bg-white dark:bg-[#30324e]/40">
          <div className="flex items-center gap-3">
            <span className="bg-white rounded-md p-1 border border-gray-300 dark:border-gray-600 flex-shrink-0 shadow-sm">
              <Image
                src={`https://firebasestorage.googleapis.com/v0/b/abillion-analytics.appspot.com/o/${selectedCompany}.jpg?alt=media`}
                alt={selectedCompany}
                width={40}
                height={40}
                unoptimized
                className="h-8 w-auto object-contain"
              />
            </span>

            <h2 className="text-xl font-bold dark:text-white truncate hidden md:block">
              {selectedCompany} | Business Research
            </h2>
          </div>
          <div className="flex items-start gap-2">
            <div className="flex flex-col items-end gap-1">
              {updatedAt && (
                <span className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-500 dark:bg-[#30324e]/80 dark:text-gray-300 border border-gray-200 dark:border-transparent">
                  Last Updated:{" "}
                  {moment
                    .tz(updatedAt, "Asia/Kolkata")
                    .format("DD-MM-YYYY HH:mm:ss")}
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-[11px] leading-[11px] font-medium text-gray-400 dark:text-gray-400">
                <AlertTriangle
                  className="block h-[11px] w-[11px] shrink-0"
                  strokeWidth={2}
                  aria-hidden="true"
                />
                Few information has been sourced using AI.
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShow(false)}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-white dark:hover:bg-gray-700 p-2 rounded-full transition-colors shrink-0 cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          <CompanyBusinessInsight
            selectedCompany={selectedCompany}
            onRecordLoaded={setUpdatedAt}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(WhatCompanyDoesModal);
